// ---------------------------------------------------------------------------
// RGSEVA SSO client — the ORG-STANDARD snippet. Copy this file into any app that
// should honour the shared login.rgseva.com session, adjust only the app-JWT
// exchange at the bottom of getSsoToken() to your app's login shape.
// ---------------------------------------------------------------------------
//
// How the system fits together:
//
//   login.rgseva.com  =  login-frontend SPA + login-service (Go). It owns the ONLY
//   sign-in UI in the organization and an HttpOnly `icm_sso` cookie (domain
//   .rgseva.com in prod). Three endpoints matter to an app:
//
//     POST /sso/token   cookie -> fresh RS256 identity token, or 401 {code:"NO_SESSION"}
//     POST /sso/login   (used by the login page itself, never by apps)
//     POST /sso/logout  revoke the browser's SSO session
//
//   Every /sso POST needs credentials:"include" (the cookie) AND the header
//   "X-Requested-With: sso-client" (the service's CSRF guard). Our app origin must be
//   in login-service's ALLOWED_ORIGINS for the CORS preflight to pass.
//
//   The RS256 token is NOT an app session. The app's own backend exchanges it:
//   POST /signin/apiAuthSso {ssoToken} -> our regular JWT (it verifies the signature
//   against login.rgseva.com/.well-known/jwks.json and looks the user up by email).
//
// Two flows an app uses:
//
//   Silent  — getSsoToken() on the login page's mount: if the browser already holds
//             the org session, the user is signed in with zero clicks.
//   Popup   — signInViaSso() behind a "Sign in with RGSEVA ID" button: opens the
//             login page as a popup with ?mode=popup&origin=<us>; the page posts
//             {type:"rgseva-sso:complete"} back (a SIGNAL ONLY — tokens never travel
//             via postMessage) and we then call getSsoToken() ourselves. If the popup
//             is blocked we fall back to a full-page redirect with ?next=<here>.
//
// Configuration (both must be set or SSO is disabled and SSO_ENABLED is false):
//
//   VITE_SSO_API_URL   base of the /sso endpoints (dev http://localhost:8790,
//                      prod https://login.rgseva.com)
//   VITE_SSO_PAGE_URL  base of the login PAGE (dev http://localhost:5176 — the
//                      login-frontend dev server; prod https://login.rgseva.com,
//                      i.e. the same value as the API base)
// ---------------------------------------------------------------------------

import { API_BASE, type LoginResult } from "@/lib/api";

const stripSlash = (s: string | undefined) => (s ?? "").replace(/\/+$/, "");

const SSO_API_URL = stripSlash(import.meta.env.VITE_SSO_API_URL as string | undefined);
const SSO_PAGE_URL = stripSlash(import.meta.env.VITE_SSO_PAGE_URL as string | undefined);

/** False until BOTH VITE_SSO_API_URL and VITE_SSO_PAGE_URL are configured. */
export const SSO_ENABLED = Boolean(SSO_API_URL && SSO_PAGE_URL);

/** Mandatory on every /sso POST: cookie + the CSRF-guard header. */
const SSO_FETCH_INIT: RequestInit = {
  method: "POST",
  credentials: "include",
  headers: { "X-Requested-With": "sso-client" },
};

/**
 * The SSO session exists and is valid, but OUR backend has no active account for that
 * email — a real error the user should see (vs. null = simply not signed in).
 */
export class NoAppAccountError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoAppAccountError";
  }
}

/**
 * True when this page can actually carry the SSO session cookie — i.e. it is served from the
 * cookie's own site (rgseva.com or a subdomain). Compared against the configured SSO host, so
 * nothing is hard-coded.
 */
function sameSiteAsSso(): boolean {
  try {
    const ssoHost = new URL(SSO_API_URL).hostname;              // e.g. login.rgseva.com
    const cookieSite = ssoHost.split(".").slice(-2).join(".");  // e.g. rgseva.com
    const here = window.location.hostname;
    return here === cookieSite || here.endsWith(`.${cookieSite}`);
  } catch {
    return false;
  }
}

/**
 * Silent sign-in: org SSO session -> this app's own JWT.
 *
 * Returns null when there is nothing to do (no SSO session, SSO service unreachable,
 * or SSO disabled by config). Throws NoAppAccountError when the SSO identity exists
 * but has no account in this app. Any other exchange failure throws a plain Error.
 */
export async function getSsoToken(): Promise<LoginResult | null> {
  if (!SSO_ENABLED) return null;
  // The icm_sso cookie is Domain=rgseva.com, SameSite=Lax, so it is only ever sent on a request
  // from that site. Anywhere else — localhost, or this app's own production domain — this call
  // is guaranteed to come back 401 NO_SESSION; asking anyway only costs a round trip and puts a
  // confusing 401 in the console. Off-site sign-in is the redirect + one-time code flow instead.
  if (!sameSiteAsSso()) return null;

  // 1) Ask login-service for a fresh RS256 identity token (the icm_sso cookie decides).
  let ssoToken: string;
  try {
    const res = await fetch(`${SSO_API_URL}/sso/token`, SSO_FETCH_INIT);
    if (!res.ok) return null; // 401 {code:"NO_SESSION"} — not signed in org-wide
    const data = await res.json();
    if (!data?.accessToken) return null;
    ssoToken = data.accessToken as string;
  } catch {
    return null; // network failure — behave as "not signed in", never block the form
  }

  // 2) Exchange it at OUR backend for the app JWT. (Direct fetch on API_BASE, the same
  //    pattern as the other /signin/* exchanges in lib/api.ts — no bearer token yet.)
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiAuthSso`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ssoToken }),
    });
  } catch {
    return null; // our backend is down — let the normal login form handle it
  }
  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (res.status === 401) {
    // Signed in to RGSEVA, but no active account in this app (server message says so).
    throw new NoAppAccountError(
      (data as { message?: string }).message ?? "No account for your ICM ID in this app."
    );
  }
  if (!res.ok || !(data as { accessToken?: string }).accessToken) {
    throw new Error((data as { message?: string }).message ?? "ICM sign-in failed.");
  }
  const d = data as { accessToken: string; username?: string; displayName?: string };
  return {
    token: d.accessToken,
    username: d.username ?? "",
    displayName: d.displayName ?? d.username ?? "",
    roles: [], // filled in by the app's /api/me profile refresh after signInWithToken
  };
}

/**
 * Interactive sign-in: send the WHOLE PAGE to login.rgseva.com, which returns here with a
 * one-time code that the login page redeems at our backend.
 *
 * This replaced a popup. The popup signed the user in up there and then asked /sso/token for
 * the result — a cookie-bearing request that, from any origin outside rgseva.com, never carries
 * the cookie. It came back 401 NO_SESSION and the button appeared to do nothing. The redirect
 * needs no cross-site cookie at all, so it works on every origin.
 *
 * Unlike redirectToSso this is an explicit click, so it ignores (and resets) the bounce guard.
 */
export function signInWithSsoRedirect(): boolean {
  if (!SSO_ENABLED) return false;
  clearSsoBounce();
  return redirectToSso();
}

/**
 * Key marking that this tab has already been bounced to login.rgseva.com and come back
 * without a session. Without it, a visitor who has no account in THIS app would ping-pong
 * between the two sites forever.
 */
const BOUNCED_KEY = "rgseva-sso:bounced";

/**
 * Send the whole page to login.rgseva.com to sign in, returning here afterwards.
 *
 * Used when SSO is the only way in: rendering a login page whose sole control is a
 * "Sign in with RGSEVA ID" button just adds a click, so go straight there.
 *
 * Returns false when the bounce was suppressed (already tried, or SSO disabled), which
 * tells the caller to render the page normally instead.
 */
export function redirectToSso(): boolean {
  if (!SSO_ENABLED) return false;
  try {
    if (sessionStorage.getItem(BOUNCED_KEY)) return false;
    sessionStorage.setItem(BOUNCED_KEY, "1");
  } catch {
    // Private mode or blocked storage: without a guard a loop is possible, so do not
    // redirect at all and let the normal page render.
    return false;
  }
  // response=code makes login.rgseva.com hand back a one-time code on the return URL
  // instead of relying on its cookie being readable here. That cookie is SameSite=Lax
  // and scoped to .rgseva.com, so on any other origin — a developer's localhost, or a
  // future app on another domain — it is third-party and simply never sent. The code
  // path works everywhere, so it is used everywhere rather than only where it is needed.
  window.location.href =
    `${SSO_PAGE_URL}/?response=code&next=${encodeURIComponent(window.location.href)}`;
  return true;
}

/**
 * Key marking that the user signed out HERE, deliberately. Without it, sign-out is a loop: the
 * login page's silent probe / bounce finds an org session and signs the user straight back in.
 */
const SIGNED_OUT_KEY = "rgseva-sso:signed-out";

/** Set on sign-out, BEFORE leaving the page, so the login screen shows its form. */
export function markSsoSignedOut(): void {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    // Also block the automatic full-page bounce to login.rgseva.com, which would sign the
    // user back in just as effectively as the silent probe.
    sessionStorage.setItem(BOUNCED_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** True while the user is on the login page after signing out on purpose. */
export function ssoSignedOut(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Hand the whole browser to login.rgseva.com to END the org-wide session, then come back to
 * `returnTo` — the same round trip the Grails app makes (?logout=1&next=...).
 *
 * Why a navigation and not the ssoLogout() fetch: the icm_sso cookie is Domain=rgseva.com,
 * Path=/sso, SameSite=Lax. From any origin outside rgseva.com a fetch is a cross-site
 * subresource request and the cookie is NOT sent, so the logout revoked nothing and the user
 * stayed signed in to the organisation. A top-level navigation IS sent the cookie, so the login
 * page can revoke the session same-origin. Returns false when SSO is not configured.
 */
export function redirectToSsoLogout(returnTo: string): boolean {
  if (!SSO_ENABLED) return false;
  window.location.replace(`${SSO_PAGE_URL}/?logout=1&next=${encodeURIComponent(returnTo)}`);
  return true;
}

/** Called once a session exists, so a later sign-out can bounce again. */
export function clearSsoBounce(): void {
  try {
    sessionStorage.removeItem(BOUNCED_KEY);
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Redeem a one-time code left on the URL by login.rgseva.com.
 *
 * The exchange happens on OUR server (SigninController.apiAuthSsoCode), not here: the
 * code is useless to a browser, and the back-channel call is what makes this work with
 * no cross-site cookie at all.
 *
 * Returns null when there is no code to redeem. The parameter is stripped from the
 * address bar either way, so a refresh cannot try to spend an already-spent code.
 */
export async function redeemSsoCode(): Promise<LoginResult | null> {
  if (!SSO_ENABLED) return null;

  const url = new URL(window.location.href);
  const code = url.searchParams.get("sso_code");
  if (!code) return null;

  url.searchParams.delete("sso_code");
  window.history.replaceState({}, "", url.toString());

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiAuthSsoCode`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ssoCode: code, origin: window.location.origin }),
    });
  } catch {
    return null;
  }
  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (res.status === 401) {
    throw new NoAppAccountError(
      (data as { message?: string }).message ?? "No account for your ICM ID in this app."
    );
  }
  if (!res.ok || !(data as { accessToken?: string }).accessToken) {
    throw new Error((data as { message?: string }).message ?? "ICM sign-in failed.");
  }
  const d = data as {
    accessToken: string;
    username?: string;
    displayName?: string;
    roles?: string[];
  };
  return {
    token: d.accessToken,
    username: d.username ?? "",
    displayName: d.displayName ?? d.username ?? "",
    roles: d.roles ?? [],
  };
}

/**
 * Revoke the org-wide SSO session WITHOUT leaving this page, and prove that it took.
 *
 * Possible only when this app is served from the cookie's own site (*.rgseva.com). icm_sso is
 * SameSite=Lax, which withholds it from CROSS-SITE subresource requests but DOES send it on a
 * cross-origin same-SITE one — which is what this is. From anywhere else (a developer's
 * localhost, rgmdatabase.com) the call would carry no cookie and revoke nothing, so this returns
 * false and the caller falls back to redirectToSsoLogout().
 *
 * Preferred over that redirect wherever it works, because of installed PWAs. login.rgseva.com is
 * outside this app's manifest scope, so Android Chrome does not navigate a standalone window
 * there at all: it opens a Custom Tab on top and leaves this page — still showing a signed-in
 * app — underneath, and the ?next= return lands in that tab rather than back in the PWA. Sign-out
 * therefore appeared to do nothing on an installed phone app while working on every desktop,
 * which has no scope to leave. Revoking in the background keeps the whole sequence in THIS
 * window, so the page we replace afterwards is the one the user is looking at.
 *
 * Returns true only when a follow-up probe finds no session left. Never throws.
 */
export async function ssoLogoutSameSite(): Promise<boolean> {
  if (!SSO_ENABLED || !sameSiteAsSso()) return false;
  // Twice: a revoke can race a token issued a moment earlier.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await fetch(`${SSO_API_URL}/sso/logout`, SSO_FETCH_INIT);
      const probe = await fetch(`${SSO_API_URL}/sso/token`, SSO_FETCH_INIT);
      if (!probe.ok) return true; // 401 {code:"NO_SESSION"} — nothing left to revoke
    } catch {
      return false; // never reached the service — hand over to the redirect instead
    }
  }
  return false;
}
